import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  Chip,
} from '@mui/material'
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material'

const MetricCard = ({
  title,
  value,
  change = 0,
  icon,
  color = 'primary',
  isLoading = false,
  subtitle = null
}) => {
  const isPositiveChange = change >= 0
  
  const getGradientColor = (color) => {
    const gradients = {
      primary: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      success: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
      warning: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
      secondary: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
      error: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
    }
    return gradients[color] || gradients.primary
  }

  // Adjust height if subtitle is present
  const cardHeight = subtitle ? 160 : 140

  if (isLoading) {
    return (
      <Card sx={{ height: cardHeight, position: 'relative', overflow: 'hidden' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="80%" height={40} sx={{ mt: 1 }} />
              <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
            </Box>
            <Skeleton variant="circular" width={48} height={48} />
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card
      sx={{
        height: cardHeight,
        position: 'relative',
        overflow: 'hidden',
        background: getGradientColor(color),
        color: 'white',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
        }}
      />
      
      <CardContent sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', height: '100%' }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                opacity: 0.9,
                fontSize: '0.875rem',
                fontWeight: 500,
                mb: 1,
              }}
            >
              {title}
            </Typography>
            
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                mb: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' },
              }}
            >
              {value}
            </Typography>
            
            {change !== 0 && (
              <Chip
                icon={isPositiveChange ? <TrendingUpIcon /> : <TrendingDownIcon />}
                label={`${isPositiveChange ? '+' : ''}${change}%`}
                size="small"
                sx={{
                  backgroundColor: isPositiveChange
                    ? 'rgba(76, 175, 80, 0.2)'
                    : 'rgba(244, 67, 54, 0.2)',
                  color: isPositiveChange ? '#4caf50' : '#f44336',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  '& .MuiChip-icon': {
                    color: isPositiveChange ? '#4caf50' : '#f44336',
                  },
                }}
              />
            )}

            {/* Subtitle - Platform distribution etc. */}
            {subtitle && (
              <Box sx={{ mt: 1 }}>
                {subtitle}
              </Box>
            )}
          </Box>
          
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(10px)',
            }}
          >
            {React.cloneElement(icon, { 
              sx: { fontSize: 24, color: 'white' } 
            })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default MetricCard